'use client';

/* eslint-disable @next/next/no-img-element */
import ProductCard, { type Product } from '@/components/Product/ProductCard';
import { useValidExposure } from '@/hooks/useValidExposure';
import { EProductImageType } from '@/utils/enums/goods';
import { useRef, useState } from 'react';
import type { Swiper as SwiperInstance } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { resolveCollaborationAssetUrl } from '../runtimeData';
import type { CollaborationSeriesData, CollaborationSeriesItemData } from '../types';
import styles from '../CollaborationPage.module.css';

type SeriesProductTrackingHandler = (
  product: unknown,
  mktIndex: number,
  productIndex: number,
  seriesName: string,
) => void;

const COLLABORATION_SERIES_HOVER_RULE = [EProductImageType.PRODUCT];

function seriesAnchor(sort: number) {
  return `collaboration-series-${sort}`;
}

function getStickyHeaderHeight(): number {
  return Array.from(
    document.querySelectorAll<HTMLElement>(
      '[class~="sticky"][class~="top-0"], [class~="fixed"][class~="top-0"]',
    ),
  ).reduce((height, element) => {
    const rect = element.getBoundingClientRect();
    return rect.top <= 0 && rect.bottom > 0 ? Math.max(height, rect.bottom) : height;
  }, 0);
}

function SeriesArrow({ direction }: { direction: 'down' | 'right' }) {
  return direction === 'down' ? (
    <svg aria-hidden="true" data-direction="down" viewBox="0 0 24 24">
      <path d="m5.75 9.75 6.25 6 6.25-6" />
    </svg>
  ) : (
    <svg aria-hidden="true" data-direction="right" viewBox="0 0 24 24">
      <path d="m9 5.75 6 6.25-6 6.25" />
    </svg>
  );
}

function SeriesNavCard({
  item,
  index,
  isWeb,
  isActive,
  onNavigate,
  onExposure,
}: {
  item: CollaborationSeriesItemData;
  index: number;
  isWeb: boolean;
  isActive: boolean;
  onNavigate: (sort: number, name: string, index: number) => void;
  onExposure?: (name: string, index: number) => void;
}) {
  const { ref } = useValidExposure({
    callback: () => onExposure?.(item.name, index),
    threshold: 0.8,
    minDuration: 500,
    ts: `${item.sort}-${item.name}-${isWeb ? 'pc' : 'wap'}`,
  });

  return (
    <button
      ref={ref}
      type="button"
      className={isActive ? styles.seriesCardActive : styles.seriesCard}
      data-offset={index % 2 === 0 ? 'up' : 'down'}
      data-series-nav-sort={item.sort}
      aria-current={isActive ? 'true' : undefined}
      onClick={() => onNavigate(item.sort, item.name, index)}
    >
      <img
        src={resolveCollaborationAssetUrl(isWeb ? item.pcCardImage : item.wapCardImage)}
        alt=""
        loading="lazy"
        decoding="async"
      />
      <span className={styles.seriesCardLabel}>
        <span className={styles.seriesCardText}>{item.name}</span>
        <SeriesArrow direction={isWeb ? 'down' : 'right'} />
      </span>
    </button>
  );
}

function isSunglassesProduct(product: Record<string, unknown>): boolean {
  const prescriptionTypes = Array.isArray(product.prescription_type)
    ? product.prescription_type
    : [];
  return (
    Number(product.is_sunglasses) === 1 ||
    Number(product.is_web_sunglasses_classification) === 1 ||
    prescriptionTypes.some(
      (type) => typeof type === 'string' && type.toLowerCase().includes('sunglasses'),
    )
  );
}

function SeriesSceneCopy({
  title,
  subTitle,
  className = '',
}: {
  title: string;
  subTitle: string;
  className?: string;
}) {
  if (!title && !subTitle) return null;

  return (
    <div className={`${styles.seriesSceneCopy} ${className}`} data-has-subtitle={Boolean(subTitle)}>
      {title && <h3>{title}</h3>}
      {subTitle && <p>{subTitle}</p>}
    </div>
  );
}

function ResolvedProductCard({
  product,
  index,
  mktIndex,
  seriesName,
  isWeb,
  onProductClick,
  onProductExposure,
  onProductCollect,
}: {
  product: Record<string, unknown>;
  index: number;
  mktIndex: number;
  seriesName: string;
  isWeb: boolean;
  onProductClick?: SeriesProductTrackingHandler;
  onProductExposure?: SeriesProductTrackingHandler;
  onProductCollect?: (product: unknown, index: number, collectType: string) => void;
}) {
  return (
    <ProductCard
      product={product as Product}
      index={index}
      showTryOn={false}
      showWish
      showCorrelation
      correlationDisplayType={
        isSunglassesProduct(product) ? 'product-thumbnail' : 'square-color-swatch'
      }
      correlationHoverBehavior={isWeb ? 'preview' : undefined}
      correlationIndicatorMotion={isWeb ? 'static' : 'slide'}
      showName={false}
      showReviewStar={false}
      showMember={false}
      showTag={false}
      imageHoverRule={COLLABORATION_SERIES_HOVER_RULE}
      hoverProductImagePriority="gray-second-only"
      className={styles.seriesProductCard}
      imageClassName={styles.seriesProductImage}
      productInfoClassName={styles.seriesProductInfo}
      handelClick={(currentIndex, currentProduct) =>
        onProductClick?.(currentProduct, mktIndex, currentIndex, seriesName)
      }
      onValidExposure={(currentIndex, currentProduct) =>
        onProductExposure?.(currentProduct, mktIndex, currentIndex, seriesName)
      }
      wished={(currentProduct, currentIndex) =>
        onProductCollect?.(currentProduct, currentIndex, '收藏')
      }
      removed={(currentProduct, currentIndex) =>
        onProductCollect?.(currentProduct, currentIndex, '取消收藏')
      }
    />
  );
}

function SeriesFloor({
  item,
  index,
  isWeb,
  onProductClick,
  onProductExposure,
  onProductCollect,
}: {
  item: CollaborationSeriesItemData;
  index: number;
  isWeb: boolean;
  onProductClick?: SeriesProductTrackingHandler;
  onProductExposure?: SeriesProductTrackingHandler;
  onProductCollect?: (product: unknown, index: number, collectType: string) => void;
}) {
  const image = resolveCollaborationAssetUrl(isWeb ? item.pcSceneImage : item.wapSceneImage);
  const floorTitle = item.name;
  return (
    <article
      id={seriesAnchor(item.sort)}
      className={styles.seriesFloor}
      data-series-sort={item.sort}
      data-layout={isWeb ? 'pc-full-bleed' : 'wap-stack'}
      data-scene-side={index % 2 === 0 ? 'left' : 'right'}
      data-product-layout={
        item.products.length === 1 ? 'single' : item.products.length > 1 ? 'multiple' : 'empty'
      }
    >
      <div className={styles.seriesScene}>
        {image && <img src={image} alt={item.name} loading="lazy" decoding="async" />}
        {!isWeb && <SeriesSceneCopy title={floorTitle} subTitle={item.subTitle} />}
        {isWeb && (
          <SeriesSceneCopy
            title={floorTitle}
            subTitle={item.subTitle}
            className={styles.seriesSceneCopyCompact}
          />
        )}
      </div>
      <div className={styles.seriesProductArea}>
        {isWeb && (
          <SeriesSceneCopy
            title={floorTitle}
            subTitle={item.subTitle}
            className={styles.seriesSceneCopyDesktop}
          />
        )}
        <div className={styles.seriesProducts}>
          {item.products.map((product, productIndex) => (
            <ResolvedProductCard
              key={`${product.shopSku}-${productIndex}`}
              product={product}
              index={productIndex}
              mktIndex={index}
              seriesName={item.name}
              isWeb={isWeb}
              onProductClick={onProductClick}
              onProductExposure={onProductExposure}
              onProductCollect={onProductCollect}
            />
          ))}
        </div>
      </div>
    </article>
  );
}

export default function CollaborationSeries({
  data,
  isWeb,
  onNavClick,
  onNavExposure,
  onProductClick,
  onProductExposure,
  onProductCollect,
}: {
  data: CollaborationSeriesData;
  isWeb: boolean;
  onNavClick?: (name: string, index: number) => void;
  onNavExposure?: (name: string, index: number) => void;
  onProductClick?: SeriesProductTrackingHandler;
  onProductExposure?: SeriesProductTrackingHandler;
  onProductCollect?: (product: unknown, index: number, collectType: string) => void;
}) {
  const [activeSort, setActiveSort] = useState(data.items[0]?.sort ?? 0);
  const swiperRef = useRef<SwiperInstance | null>(null);
  const gestureStartIndexRef = useRef(0);
  const gestureMovedRef = useRef(false);
  const canLoop = data.items.length >= 4;

  if (data.items.length === 0) return null;

  const navigateTo = (sort: number, name: string, index: number) => {
    setActiveSort(sort);
    onNavClick?.(name, index);
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const behavior: ScrollBehavior = reducedMotion ? 'auto' : 'smooth';
    const target = document.getElementById(seriesAnchor(sort));
    if (target) {
      window.scrollTo({
        behavior,
        top: Math.max(
          0,
          window.scrollY + target.getBoundingClientRect().top - getStickyHeaderHeight(),
        ),
      });
    }
    if (!isWeb) {
      const swiper = swiperRef.current;
      if (canLoop) swiper?.slideToLoop(index, reducedMotion ? 0 : 300);
      else swiper?.slideTo(index, reducedMotion ? 0 : 300);
    }
  };

  return (
    <section className={styles.seriesSection}>
      {(data.title || data.subTitle) && (
        <header className={styles.sectionHeading}>
          {data.title && <h2>{data.title}</h2>}
          {data.subTitle && <p>{data.subTitle}</p>}
        </header>
      )}
      <nav className={styles.seriesNav} aria-label={data.title}>
        {isWeb ? (
          <div className={styles.seriesRail}>
            {data.items.map((item, index) => (
              <SeriesNavCard
                key={item.sort}
                item={item}
                index={index}
                isWeb
                isActive={activeSort === item.sort}
                onNavigate={navigateTo}
                onExposure={onNavExposure}
              />
            ))}
          </div>
        ) : (
          <Swiper
            className={styles.seriesCarousel}
            slidesPerView="auto"
            centeredSlides
            slidesPerGroup={1}
            spaceBetween={-12}
            loop={canLoop}
            rewind={!canLoop}
            followFinger={false}
            shortSwipes={false}
            longSwipes={false}
            focusableElements="input, select, option, textarea, video, label"
            onTouchStart={(swiper) => {
              gestureStartIndexRef.current = swiper.realIndex;
              gestureMovedRef.current = false;
            }}
            onSliderMove={() => {
              gestureMovedRef.current = true;
            }}
            onTouchEnd={(swiper, event) => {
              if (
                !gestureMovedRef.current ||
                !swiper.swipeDirection ||
                Math.abs(swiper.touches.diff) < 20 ||
                event.type === 'touchcancel' ||
                event.type === 'pointercancel'
              )
                return;
              const step = swiper.swipeDirection === 'next' ? 1 : -1;
              const nextIndex =
                (gestureStartIndexRef.current + step + data.items.length) % data.items.length;
              // Swiper 的默认释放逻辑可能跨多张；释放完成后只定位到相邻系列。
              queueMicrotask(() => {
                if (swiper.destroyed) return;
                const speed = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
                  ? 0
                  : 300;
                if (canLoop) swiper.slideToLoop(nextIndex, speed);
                else swiper.slideTo(nextIndex, speed);
              });
            }}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            onSlideChange={(swiper) => {
              const item = data.items[swiper.realIndex];
              if (item) setActiveSort(item.sort);
            }}
          >
            {data.items.map((item, index) => (
              <SwiperSlide key={item.sort} className={styles.seriesCarouselSlide}>
                <SeriesNavCard
                  item={item}
                  index={index}
                  isWeb={false}
                  isActive={activeSort === item.sort}
                  onNavigate={navigateTo}
                  onExposure={onNavExposure}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </nav>
      <div className={styles.seriesFloors}>
        {data.items.map((item, index) => (
          <SeriesFloor
            key={item.sort}
            item={item}
            index={index}
            isWeb={isWeb}
            onProductClick={onProductClick}
            onProductExposure={onProductExposure}
            onProductCollect={onProductCollect}
          />
        ))}
      </div>
    </section>
  );
}
